import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BellOff,
  Check,
  CheckCheck,
  Handshake,
  FileText,
  CreditCard,
  Sparkles,
  AlertTriangle,
  Loader2,
  Inbox,
  CircleCheckBig,
  Clock,
  Zap,
  TrendingUp,
  MoreHorizontal,
  ArrowRight,
  Wallet,
  DollarSign,
  Percent,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { useNotifications } from "@/hooks/use-notifications";
import { useAuth } from "@/hooks/use-auth";
import { useRecoveryFacts } from "@/hooks/use-recovery-facts";

import {
  applyNotificationFilters,
  isDismissed,
  notificationCategory,
  notificationState as readState,
  type CategoryFilter,
  type DateFilter,
} from "@/lib/notifications/model";
import {
  BUCKET_LABEL,
  TONE_STYLES,
  buildActivitySummary,
  fileNumber,
  formatCurrency,

  notificationHeadline,
  notificationStatus,
  notificationTone,
  readStateLabel,
  readStateRailClass,
  primaryAction,
  requiresAction,
  secondaryActions,
  smartMetrics,
  summaryLines,
  timeBucket,
  valueChips,
  vendorName,
  type SmartMetric,
  type TimeBucket,
} from "@/lib/notifications/presentation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Notification } from "@/lib/db/models";


export const Route = createFileRoute("/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications | Invoice Intelligence AI Activity Center" },
      {
        name: "description",
        content:
          "Track review progress, negotiation activity, Total Supported Recovery, and items that need your attention in one activity center.",
      },
      { property: "og:title", content: "Notifications | Activity Center" },
      {
        property: "og:description",
        content:
          "Review progress, negotiation activity, and Total Supported Recovery across all of your claims.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <AuthGate>
      <NotificationsPage />
    </AuthGate>
  ),
});

const PRIMARY_FILTERS: { key: CategoryFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "review", label: "Reviews" },
  { key: "report", label: "Reports" },
  { key: "negotiation", label: "Negotiations" },
  { key: "payment", label: "Payments" },
  { key: "billing", label: "Billing" },
];

const TIME_FILTERS: { key: DateFilter; label: string }[] = [
  { key: "any", label: "Any Time" },
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "earlier", label: "Earlier" },
];

const BUCKET_ORDER: TimeBucket[] = ["today", "yesterday", "week", "earlier"];

function NotificationsPage() {
  const { user } = useAuth();
  const {
    items,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    dismiss,
    undismiss,
    reload,
  } = useNotifications();
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("any");
  const [lastDismissed, setLastDismissed] = useState<Notification | null>(null);

  /** Non-dismissed notifications — the working set for every metric. */
  const visible = useMemo(() => items.filter((n) => !isDismissed(n)), [items]);

  /** Category filter FIRST, then the date filter. */
  const filtered = useMemo(
    () => applyNotificationFilters(items, category, dateFilter),
    [items, category, dateFilter],
  );


  const groups = useMemo(() => {
    const map = new Map<TimeBucket, Notification[]>();
    for (const n of filtered) {
      const b = timeBucket(n.created_at);
      const arr = map.get(b) ?? [];
      arr.push(n);
      map.set(b, arr);
    }
    return BUCKET_ORDER.filter((b) => (map.get(b)?.length ?? 0) > 0).map((b) => ({
      bucket: b,
      rows: map.get(b)!,
    }));
  }, [filtered]);

  const { facts } = useRecoveryFacts(
    useMemo(() => visible.map((n) => n.review_id), [visible]),
    user?.id ?? null,
  );
  const summary = useMemo(() => buildActivitySummary(visible, facts), [visible, facts]);

  const metrics = useMemo(() => smartMetrics(summary), [summary]);
  const allClear =
    visible.length > 0 && summary.urgent === 0 && visible.filter(requiresAction).length === 0;

  const lastUpdated = visible[0]?.created_at ?? null;

  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main className="container-page py-8">
        <Breadcrumbs items={[{ label: "Notifications" }]} />

        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-navy">Notifications</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Priority, progress, and recovery potential across every claim.
            </p>
          </div>
          <button
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium text-navy transition duration-200 hover:bg-accent hover:shadow-[var(--shadow-card)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CheckCheck className="h-4 w-4" /> Mark all as read
          </button>
        </div>

        <ActivitySummaryCard summary={summary} lastUpdated={lastUpdated} loading={loading} />

        <SmartMetricsBar metrics={metrics} allClear={allClear} loading={loading} />


        <div className="mt-6 flex flex-wrap items-center gap-3">
          <SegmentedControl
            options={PRIMARY_FILTERS}
            value={category}
            onChange={setCategory}
            badge={{ unread: unreadCount }}
            ariaLabel="Filter by category"
          />
          <SegmentedControl
            options={TIME_FILTERS}
            value={dateFilter}
            onChange={setDateFilter}
            ariaLabel="Filter by date"
          />
        </div>

        {lastDismissed && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border bg-card px-3.5 py-2.5 text-xs text-muted-foreground">
            <span>Notification dismissed. It stays in your history and is not deleted.</span>
            <button
              onClick={() => {
                void undismiss(lastDismissed.id);
                setLastDismissed(null);
              }}
              className="rounded-md border px-2 py-1 text-[11px] font-semibold text-navy hover:bg-accent"
            >
              Undo
            </button>
          </div>
        )}


        <div className="mt-6">
          {loading ? (
            <div className="flex items-center gap-3 rounded-xl border bg-card p-10 text-sm text-muted-foreground shadow-[var(--shadow-card)]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading activity…
            </div>
          ) : error ? (
            <div className="flex flex-col items-start gap-3 rounded-xl border bg-card p-8 shadow-[var(--shadow-card)]">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                We couldn't load your notifications.
              </div>
              <p className="text-xs text-muted-foreground">{error}</p>
              <button
                onClick={reload}
                className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Try again
              </button>
            </div>
          ) : groups.length === 0 ? (
            <EmptyState filter={category} />
          ) : (
            <div className="space-y-8">
              {groups.map((g) => (
                <section key={g.bucket}>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {BUCKET_LABEL[g.bucket]}
                    </h2>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      {g.rows.length}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div className="mt-3 space-y-3">
                    {g.rows.map((n) => (
                      <NotificationCard
                        key={n.id}
                        n={n}
                        canonicalRecovery={
                          (n.review_id ? facts.get(n.review_id)?.recovery : null) ?? null
                        }
                        onRead={() => markAsRead(n.id)}
                        onDismiss={() => {
                          void dismiss(n.id);
                          setLastDismissed(n);
                        }}

                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/* ---------------------------------- summary --------------------------------- */

function ActivitySummaryCard({
  summary,
  lastUpdated,
  loading,
}: {
  summary: ReturnType<typeof buildActivitySummary>;
  lastUpdated: string | null;
  loading: boolean;
}) {
  const lines = summaryLines(summary);
  const headlineMetric =
    summary.estimatedSavings > 0
      ? { label: "Estimated recovery identified", value: formatCurrency(summary.estimatedSavings) }
      : summary.invoiceValueReviewed > 0
        ? { label: "Invoiced charges reviewed", value: formatCurrency(summary.invoiceValueReviewed) }
        : summary.reviewsCompleted > 0
          ? { label: "Professional reviews completed", value: String(summary.reviewsCompleted) }
          : null;

  return (
    <div className="mt-6 animate-fade-in overflow-hidden rounded-2xl border border-navy/15 bg-navy text-navy-foreground shadow-[var(--shadow-elevated)]">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold tracking-tight">Nova Activity Summary</p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-navy-foreground/60">
              Across all completed reviews
            </p>
          </div>
        </div>
        <p className="text-[11px] text-navy-foreground/60">
          Last updated{" "}
          {loading
            ? "…"
            : lastUpdated
              ? `${format(new Date(lastUpdated), "MMM d, yyyy · h:mm a")}`
              : "—"}
        </p>
      </div>
      <div
        className={`grid gap-5 px-6 py-5 ${headlineMetric ? "md:grid-cols-[1.4fr_1fr]" : ""}`}
      >
        {lines.length > 0 ? (
          <ul className="space-y-1.5 text-sm">
            {lines.map((l) => (
              <li key={l} className="flex items-start gap-2 text-navy-foreground/90">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-savings" aria-hidden />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-navy-foreground/80">
            No new activity to report. We'll summarize progress here as your reviews and
            negotiations advance.
          </p>
        )}
        {headlineMetric && (
          <div className="rounded-xl bg-white/5 p-4">
            <p className="text-[11px] uppercase tracking-[0.14em] text-navy-foreground/60">
              {headlineMetric.label}
            </p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-savings">
              {headlineMetric.value}
            </p>
            <p className="mt-1 text-[11px] text-navy-foreground/60">
              {summary.recoveryReviewCount > 0
                ? `From ${summary.recoveryReviewCount} completed ${summary.recoveryReviewCount === 1 ? "review" : "reviews"} with a finalized recovery estimate.`
                : "Across all completed reviews in your activity."}

            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const METRIC_TINT: Record<SmartMetric["tone"], string> = {
  savings: "bg-savings-soft text-savings",
  trust: "bg-trust-soft text-trust",
  negotiate: "bg-negotiate-soft text-negotiate",
  fasttrack: "bg-fasttrack-soft text-fasttrack",
  navy: "bg-accent text-navy",
  destructive: "bg-destructive/10 text-destructive",
};

const METRIC_ICON: Record<string, typeof Inbox> = {
  recovery: DollarSign,
  reviewed: DollarSign,
  completed: CircleCheckBig,
  avgpct: Percent,
  negotiations: Handshake,
  reports: FileText,
  awaiting: Clock,
  unread: TrendingUp,
  urgent: Zap,
  action: Clock,
};

/** Count-up runs once on first mount only. */
function useCountUp(target: number | undefined, enabled: boolean) {
  const [value, setValue] = useState(enabled && target !== undefined ? 0 : (target ?? 0));
  const done = useRef(false);
  useEffect(() => {
    if (!enabled || target === undefined || done.current) {
      if (target !== undefined) setValue(target);
      return;
    }
    done.current = true;
    if (target <= 0) return;
    const start = performance.now();
    const duration = 650;
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, enabled]);
  return value;
}

function SmartMetricCard({ metric }: { metric: SmartMetric }) {
  const Icon = METRIC_ICON[metric.key] ?? Inbox;
  const counted = useCountUp(metric.count, metric.count !== undefined);
  return (
    <div className="rounded-xl border bg-card p-3.5 shadow-[var(--shadow-card)] transition duration-200 hover:-translate-y-0.5 hover:border-navy/20 hover:shadow-[var(--shadow-elevated)]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {metric.label}
        </p>
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-full ${METRIC_TINT[metric.tone]}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <p className="mt-1.5 text-2xl font-semibold tracking-tight text-navy">
        {metric.count !== undefined ? counted : metric.value}
      </p>
    </div>
  );
}

function SmartMetricsBar({
  metrics,
  allClear,
  loading,
}: {
  metrics: SmartMetric[];
  allClear: boolean;
  loading: boolean;
}) {
  if (loading) return null;
  return (
    <div className="mt-4 space-y-3">
      {metrics.length > 0 && (
        <div
          className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${
            metrics.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"
          }`}
        >
          {metrics.map((m) => (
            <SmartMetricCard key={m.key} metric={m} />
          ))}
        </div>
      )}
      {allClear && (
        <div className="flex items-start gap-2.5 rounded-xl border border-savings/25 bg-savings-soft/60 p-3.5 text-sm text-navy">
          <CircleCheckBig className="mt-0.5 h-4 w-4 shrink-0 text-savings" aria-hidden />
          <p>
            No outstanding actions. All recent reviews have been completed successfully.
          </p>
        </div>
      )}
    </div>
  );
}


function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  badge,
  ariaLabel,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (f: T) => void;
  badge?: { unread: number };
  ariaLabel?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full border bg-card p-1 shadow-[var(--shadow-card)]"
    >
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              active
                ? "bg-navy text-navy-foreground shadow-[var(--shadow-card)]"
                : "text-muted-foreground hover:bg-accent hover:text-navy"
            }`}
          >
            {o.label}
            {o.key === "unread" && (badge?.unread ?? 0) > 0 && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                  active ? "bg-white/20" : "bg-savings-soft text-savings"
                }`}
              >
                {badge!.unread}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------ notification card --------------------------- */

function NotificationCard({
  n,
  canonicalRecovery,
  onRead,
  onDismiss,
}: {
  n: Notification;
  canonicalRecovery: number | null;
  onRead: () => void;
  onDismiss: () => void;
}) {
  const tone = notificationTone(n);
  const style = TONE_STYLES[tone];
  const status = notificationStatus(n);
  const chips = valueChips(n, { canonicalRecovery });
  const action = primaryAction(n);
  const extras = secondaryActions(n);
  const vendor = vendorName(n);
  const file = fileNumber(n);
  const headline = notificationHeadline(n);
  const state = readState(n);
  const unread = state === "unread";
  const category = notificationCategory(n);



  const Icon =
    tone === "negotiation"
      ? Handshake
      : tone === "billing"
        ? CreditCard
        : tone === "payment"
          ? Wallet
          : tone === "urgent"
            ? AlertTriangle
            : tone === "completed"
              ? CircleCheckBig
              : n.kind.startsWith("ai_") || n.kind === "review_started"
                ? Sparkles
                : FileText;

  return (
    <article
      aria-label={readStateLabel(n)}
      data-read-state={state}
      data-category={category}

      className={`group relative overflow-hidden rounded-xl border bg-card pl-5 shadow-[var(--shadow-card)] transition duration-200 hover:-translate-y-0.5 hover:border-navy/25 hover:shadow-[var(--shadow-elevated)] ${
        unread ? "border-navy/15" : ""
      }`}
    >
      {/* Left rail encodes NOTIFICATION READ STATE only (read_at), never
          report/review workflow status: red = unread, green = reviewed. */}
      <span
        data-testid="notification-rail"
        className={`absolute inset-y-0 left-0 w-[6px] rounded-r-sm ${readStateRailClass(n)}`}
        aria-hidden
      />
      <span className="sr-only" aria-live="polite">
        {readStateLabel(n)}
      </span>
      <div className="flex flex-col gap-3 p-3.5 sm:flex-row sm:items-start">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.bubble}`}
        >
          <Icon className="h-4.5 w-4.5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${style.chip}`}
            >
              {style.label}
            </span>
            <StatusBadge status={status.key} label={status.label} />
            {unread && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-destructive">
                <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden /> New
              </span>
            )}
          </div>


          <h3 className="mt-1.5 text-sm font-semibold leading-snug text-navy">{headline}</h3>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {vendor && (
              <span className="font-medium text-foreground/80">Vendor: {vendor}</span>
            )}
            {vendor && file && <span aria-hidden>·</span>}
            {file && <span className="font-mono text-[11px]">Claim: {file}</span>}
            {(vendor || file) && <span aria-hidden>·</span>}
            <span>{timeAgo(n.created_at)}</span>
          </div>

          {n.body && (
            <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{n.body}</p>
          )}

          {chips.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              {chips.map((c) => (
                <span
                  key={`${n.id}-${c.label}`}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-secondary px-2.5 py-1 text-[11px] font-medium text-navy"
                >
                  <span className="text-muted-foreground">{c.label}</span>
                  <span className="font-semibold">{c.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
          {action?.to ? (
            <Link
              to={action.to as string}
              onClick={() => unread && onRead()}
              className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-2 text-xs font-semibold text-navy-foreground transition duration-200 hover:bg-navy/90 hover:shadow-[var(--shadow-card)] group-hover:brightness-110"
            >
              {action.label} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="More actions"
              title="More Actions"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground transition duration-200 hover:border-navy/30 hover:bg-accent hover:text-navy focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-48">
              {extras.map((e) => (
                <DropdownMenuItem key={e.label} asChild>
                  <Link to={e.to as string}>{e.label}</Link>
                </DropdownMenuItem>
              ))}
              {unread && (
                <DropdownMenuItem onClick={onRead}>
                  <Check className="mr-2 h-3.5 w-3.5" /> Mark as read
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onDismiss}>Dismiss</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </article>
  );
}

function StatusBadge({ status, label }: { status: string; label: string }) {
  const cls =
    status === "completed" || status === "payment_received"
      ? "bg-savings-soft text-savings"
      : status === "requires_action"
        ? "bg-destructive/10 text-destructive"
        : status === "report_ready"
          ? "bg-trust-soft text-trust"
          : status === "negotiation_active"
            ? "bg-negotiate-soft text-negotiate"
            : status === "ai_processing"
              ? "bg-fasttrack-soft text-fasttrack"
              : "bg-muted text-muted-foreground";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function EmptyState({ filter }: { filter: CategoryFilter }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border bg-card p-14 text-center shadow-[var(--shadow-card)]">
      <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-secondary">
        <span className="absolute inset-0 animate-[pulse_3s_ease-in-out_infinite] rounded-full bg-savings/10" />
        <BellOff className="h-7 w-7 text-muted-foreground" />
      </div>
      <div className="max-w-sm">
        <p className="text-base font-semibold text-navy">
          {filter === "unread" ? "No unread notifications" : "You're all caught up."}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          We'll notify you when ClaimGuard has updates on your reviews, negotiations, invoices, or
          billing.
        </p>
      </div>
    </div>
  );
}

function timeAgo(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}
